import React, { useEffect, useMemo, useState } from "react";
import {
  getMcTrafficProbeSnapshot,
  isMcTrafficProbeEnabled,
  resetMcTrafficProbe,
} from "../utils/mcTrafficProbe";
import "./mcDevTrafficPanel.css";

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 2 : 1)} MB`;
}

function authorityLabel(key = "") {
  const value = String(key || "");
  if (/^mc-import-snapshot:pointer:/i.test(value) || /activeSnapshot__.*__ACTIVE$/i.test(value)) return "Active inventory pointer";
  if (/^mc-import-snapshot:chunk:/i.test(value) || /activeSnapshot__.*__IMPORT__.*__\d{4}$/i.test(value)) return "Inventory snapshot chunk";
  if (/^mc-import-snapshot:manifest:/i.test(value) || /activeSnapshot__.*__IMPORT__/i.test(value)) return "Inventory snapshot manifest";
  if (/^mc-import-active/i.test(value)) return "Legacy intake snapshot";
  if (/^mc-logic-pass/i.test(value)) return "Inventory engine";
  if (/^mc-storage-movement-head/i.test(value)) return "Storage movement head";
  if (/^mc-storage-movement-revision/i.test(value)) return "Storage movement revision";
  if (/^mc-storage-movement-ledger/i.test(value)) return "Legacy storage movement ledger";
  if (/^mc-storage-sync-activity/i.test(value)) return "Storage sync activity";
  if (/^mc-prepared/i.test(value)) return "Prepared inventory";
  if (/^mc-session-list$/i.test(value)) return "Session index";
  if (/^mc-session-/i.test(value)) return "Session authority";
  if (/^mc-count-ledger/i.test(value)) return "Count ledger";
  if (/^collection:morpheus:count-ledger-discovery:/i.test(value)) return "Count-ledger collection scan";
  if (/^collection:morpheus:session-detail-discovery:/i.test(value)) return "Session-detail collection scan";
  if (/^collection:/i.test(value)) return "Collection query";
  if (/^tickets:/i.test(value)) return "Support tickets";
  if (/^billing:/i.test(value)) return "Billing authority";
  if (/^callable:/i.test(value)) return "Firebase callable";
  return value || "Firestore document";
}

export default function McDevTrafficPanel({ probeId, title = "Morpheus Traffic", subtitle = "Measures this page's existing Firestore path. No probe network calls are added.", mode = "inline", badgeLabel = "" }) {
  const enabled = useMemo(() => isMcTrafficProbeEnabled(), []);
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState(() => getMcTrafficProbeSnapshot(probeId));

  useEffect(() => {
    if (!enabled) return undefined;
    setStats(getMcTrafficProbeSnapshot(probeId));
    const timer = window.setInterval(() => setStats(getMcTrafficProbeSnapshot(probeId)), open ? 350 : 900);
    return () => window.clearInterval(timer);
  }, [enabled, open, probeId]);

  if (!enabled) return null;

  const perKeyRows = Object.values(stats.byKey || {})
    .map((row) => ({
      ...row,
      estimatedTotalDocumentBytes:
        Number(row.estimatedDocumentWriteBytes || 0) + Number(row.estimatedDocumentReadBytes || 0),
      estimatedTotalTrafficBytes:
        Number(row.estimatedDocumentWriteBytes || 0) + Number(row.estimatedDocumentReadBytes || 0)
        + Number(row.callableRequestBytes || 0) + Number(row.callableResponseBytes || 0),
    }))
    .filter((row) =>
      Number(row.firestoreWrites || 0) ||
      Number(row.firestoreReads || 0) ||
      Number(row.firestoreDeletes || 0) ||
      Number(row.listenerSnapshots || 0) ||
      Number(row.callableCalls || 0)
    )
    .sort((a, b) => Number(b.estimatedTotalTrafficBytes || 0) - Number(a.estimatedTotalTrafficBytes || 0));

  const totalOps = Number(stats.firestoreWrites || 0) + Number(stats.firestoreReads || 0) + Number(stats.firestoreDeletes || 0);
  const callableBytes = Number(stats.callableRequestBytes || 0) + Number(stats.callableResponseBytes || 0);
  const hotAuthority = perKeyRows.find((row) => Number(row.estimatedTotalTrafficBytes || 0) >= 1024 * 1024) || null;
  const repeatedAuthority = perKeyRows.find((row) => Number(row.firestoreReads || 0) >= 25) || null;
  const largestDocBytes = Number(stats.largestEstimatedDocumentBytes || 0);
  const warningItems = [];
  if (largestDocBytes >= 700 * 1024) warningItems.push({ tone: "danger", text: `Very large document observed: ${formatBytes(largestDocBytes)}` });
  else if (largestDocBytes >= 250 * 1024) warningItems.push({ tone: "warn", text: `Large document observed: ${formatBytes(largestDocBytes)}` });
  if (hotAuthority) warningItems.push({ tone: "hot", text: `Hot authority: ${authorityLabel(hotAuthority.key)} · ${formatBytes(hotAuthority.estimatedTotalTrafficBytes)}` });
  if (repeatedAuthority) warningItems.push({ tone: "warn", text: `Repeated reads: ${authorityLabel(repeatedAuthority.key)} · ${Number(repeatedAuthority.firestoreReads || 0).toLocaleString()}` });

  return (
    <section className={`mc-devTraffic mc-devTraffic--${mode} ${open ? "is-open" : ""}`}>
      <button
        type="button"
        className="mc-devTraffic__toggle"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        {badgeLabel ? `Dev Traffic · ${badgeLabel} · ` : "Dev Traffic · "}{formatBytes(stats.networkPathEstimatedBytes || stats.serverPathEstimatedDocumentBytes || stats.totalEstimatedDocumentBytes)} est
      </button>

      {open ? (
        <div className="mc-devTraffic__panel">
          <header className="mc-devTraffic__header">
            <div>
              <span>Development only · local measurement</span>
              <strong>{title}</strong>
              <p>{subtitle}</p>
            </div>
            <button type="button" onClick={() => setStats(resetMcTrafficProbe(probeId))}>Reset</button>
          </header>

          {warningItems.length ? (
            <div className="mc-devTraffic__warnings" aria-label="Traffic warnings">
              {warningItems.map((item, index) => (
                <div key={`${item.tone}-${index}`} className={`mc-devTraffic__warning is-${item.tone}`}>{item.text}</div>
              ))}
            </div>
          ) : null}

          <div className="mc-devTraffic__grid">
            <div><span>Firestore operations</span><strong>{totalOps.toLocaleString()}</strong><small>reads + writes + deletes observed</small></div>
            <div><span>Firestore reads</span><strong>{Number(stats.firestoreReads || 0).toLocaleString()}</strong><small>{formatBytes(stats.estimatedDocumentReadBytes)} estimated docs</small></div>
            <div><span>Firestore writes</span><strong>{Number(stats.firestoreWrites || 0).toLocaleString()}</strong><small>{formatBytes(stats.estimatedDocumentWriteBytes)} estimated docs</small></div>
            <div><span>Firestore deletes</span><strong>{Number(stats.firestoreDeletes || 0).toLocaleString()}</strong><small>delete operations</small></div>

            <div><span>Callable functions</span><strong>{Number(stats.callableCalls || 0).toLocaleString()}</strong><small>{formatBytes(callableBytes)} request + response</small></div>
            <div><span>Callable requests</span><strong>{formatBytes(stats.callableRequestBytes)}</strong><small>serialized request payloads</small></div>
            <div><span>Callable responses</span><strong>{formatBytes(stats.callableResponseBytes)}</strong><small>serialized response payloads</small></div>
            <div><span>Total network estimate</span><strong>{formatBytes(stats.networkPathEstimatedBytes)}</strong><small>Firestore server path + callable payload</small></div>

            <div><span>Direct reads</span><strong>{Number(stats.directReads || 0).toLocaleString()}</strong><small>{formatBytes(stats.directEstimatedDocumentBytes)}</small></div>
            <div><span>Transaction reads</span><strong>{Number(stats.transactionReads || 0).toLocaleString()}</strong><small>{formatBytes(stats.transactionEstimatedDocumentBytes)}</small></div>
            <div><span>Ack reads</span><strong>{Number(stats.ackReads || 0).toLocaleString()}</strong><small>{formatBytes(stats.ackEstimatedDocumentBytes)}</small></div>
            <div><span>Committed writes</span><strong>{Number(stats.firestoreWrites || 0).toLocaleString()}</strong><small>{Number(stats.writeAttempts || 0).toLocaleString()} instrumented attempts</small></div>

            <div><span>Listener · server</span><strong>{Number(stats.listenerServerSnapshots || 0).toLocaleString()}</strong><small>{formatBytes(stats.listenerServerBytes)} estimated docs</small></div>
            <div><span>Listener · cache</span><strong>{Number(stats.listenerCacheSnapshots || 0).toLocaleString()}</strong><small>{formatBytes(stats.listenerCacheBytes)} local-cache docs</small></div>
            <div><span>Listener · pending</span><strong>{Number(stats.listenerPendingSnapshots || 0).toLocaleString()}</strong><small>{formatBytes(stats.listenerPendingBytes)} local echoes</small></div>
            <div><span>All listener callbacks</span><strong>{Number(stats.listenerSnapshots || 0).toLocaleString()}</strong><small>{formatBytes(stats.listenerBytes)} raw value path</small></div>

            <div><span>Duplicated JSON writes</span><strong>{formatBytes(stats.duplicatedJsonWriteBytes)}</strong><small>second copy stored beside value</small></div>
            <div><span>Server-path estimate</span><strong>{formatBytes(stats.serverPathEstimatedDocumentBytes)}</strong><small>writes + direct/tx/ack + server listeners</small></div>
            <div><span>All doc observations</span><strong>{formatBytes(stats.totalEstimatedDocumentBytes)}</strong><small>includes cache/pending listeners</small></div>
            <div><span>Largest estimated doc</span><strong>{formatBytes(stats.largestEstimatedDocumentBytes)}</strong><small>largest observed document payload</small></div>
          </div>

          <div className="mc-devTraffic__detailLine">
            Attempts A1 {Number(stats.attempt1 || 0)} · A2 {Number(stats.attempt2 || 0)} · A3 {Number(stats.attempt3 || 0)} · ACK mismatch {Number(stats.ackMismatches || 0)} · failures {Number(stats.failedWrites || 0)}
          </div>

          <div className="mc-devTraffic__tableWrap">
            <table className="mc-devTraffic__table">
              <thead>
                <tr>
                  <th>Authority</th>
                  <th>Reads</th>
                  <th>Writes</th>
                  <th>Deletes</th>
                  <th>Callable</th>
                  <th>Listener S/C/P</th>
                  <th>Est. docs / payload</th>
                </tr>
              </thead>
              <tbody>
                {perKeyRows.length ? perKeyRows.map((row) => (
                  <tr key={row.key}>
                    <td><strong>{authorityLabel(row.key)}</strong><small>{row.key}</small></td>
                    <td>{Number(row.firestoreReads || 0).toLocaleString()}</td>
                    <td>{Number(row.firestoreWrites || 0).toLocaleString()}</td>
                    <td>{Number(row.firestoreDeletes || 0).toLocaleString()}</td>
                    <td>{Number(row.callableCalls || 0).toLocaleString()}</td>
                    <td>{Number(row.listenerServerSnapshots || 0)}/{Number(row.listenerCacheSnapshots || 0)}/{Number(row.listenerPendingSnapshots || 0)}</td>
                    <td>{formatBytes(Number(row.estimatedTotalDocumentBytes || 0) + Number(row.callableRequestBytes || 0) + Number(row.callableResponseBytes || 0))}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="7" className="mc-devTraffic__empty">No Firestore activity observed since reset.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <p className="mc-devTraffic__footnote">
            Estimates measure Firestore document field payloads and callable request/response bodies, not billed bytes. Protocol, indexes, compression and cache behaviour are not billed exactly like these figures.
          </p>
        </div>
      ) : null}
    </section>
  );
}
